const _price = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format a dollar price with thousands separator: 1521.79 → $1,521.79.
 *  Returns "—" for null, undefined, NaN, or negative values (tbnsdata sentinel
 *  -1000000). NaN is guarded here rather than at each call site: a missing
 *  optional price coerced with Number() lands here as NaN, and Intl formats it
 *  as the literal "$NaN" — which is what the review caught on All Items. */
export function fmtPrice(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v) || v < 0) return '—';
  return _price.format(v);
}

/** Format a percent diff with sign: 12.3456 → "+12.35%", -5.1 → "-5.10%" */
export function fmtPct(v: number): string {
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

/** dd/mm/yyyy, or "—" when the value is missing. */
export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
}

/** dd/mm/yyyy, HH:MM — used where the review asked for the sync time, not just
 *  the day (item-detail history). */
export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-GB')}, ${d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Trim the bookkeeping suffix off a vendor label: "Paccar CDK, $ check" →
 * "Paccar CDK" (BOT-392).
 *
 * tbvendors.name encodes what the vendor row is *for* as well as who it is;
 * inside Price Checks the "$ check" part is true of every row, so it is pure
 * column width. Anything that isn't that suffix is left untouched.
 */
export function fmtVendor(name: string | null | undefined): string {
  if (!name) return '—';
  return name.replace(/[,\s]+\$?\s*check\s*$/i, '').trim() || name;
}

/** "unsafe" → "Unsafe", "create_failed" → "Create failed". */
export function titleCase(v: string): string {
  const s = v.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// ── en-US, for the client dashboard (BOT-451) ───────────────────────────────

const _int = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Whole number with en-US grouping: 12345 → "12,345". "—" only for a missing value; 0 is "0". */
export function fmtInt(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return _int.format(v);
}

/** Signed whole number with a real minus sign: 674 → "+674", -5 → "−5", 0 → "0". */
export function fmtSigned(v: number): string {
  if (v === 0) return '0';
  return `${v > 0 ? '+' : '−'}${_int.format(Math.abs(v))}`;
}

const _day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/**
 * A calendar day the API already computed in the client's zone (ET), as
 * "YYYY-MM-DD" → "Oct 5". Formatted in UTC on purpose: the string is a date,
 * not an instant, so no browser zone may shift it.
 */
export function fmtDay(isoDay: string): string {
  const d = new Date(`${isoDay}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? '—' : _day.format(d);
}

/** "just now", "5m ago", "2h ago", "3d ago" — relative to `now`. */
export function fmtAgo(then: Date, now: Date = new Date()): string {
  const s = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86_400)}d ago`;
}

/** A whole-day age computed by the API (ET calendar days): "today", "1 day ago", "54 days ago". */
export function fmtDaysAgo(days: number): string {
  if (days <= 0) return 'today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

const _etDateTime = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/** A client-facing instant, always in US Eastern with its zone: "Oct 6, 9:17 AM ET". */
export function fmtEtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${_etDateTime.format(d)} ET`;
}

const _dayYear = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/** A calendar day "YYYY-MM-DD" with its year: "Apr 11, 2025" (no zone shift, like fmtDay). */
export function fmtDayYear(isoDay: string | null | undefined): string {
  if (!isoDay) return '—';
  const d = new Date(`${isoDay}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? '—' : _dayYear.format(d);
}

const _etTime = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });

/** The ET time of day of an instant: "1:30 PM ET". */
export function fmtEtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${_etTime.format(d)} ET`;
}

const _etDay = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });

/** The ET calendar day of an instant: "Sep 12". */
export function fmtEtDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : _etDay.format(d);
}

/** A length of time in hours and minutes: "3 h 35 m", "45 m", "2 d 4 h". */
export function fmtDuration(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 60) return `${m} m`;
  const h = Math.floor(m / 60);
  if (h < 48) return m % 60 ? `${h} h ${m % 60} m` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}
